import React, { useEffect, useState } from 'react';
import apiService from './apiService';
import './styles/Dashboard.css';

const Dashboard = () => {
  const APP_BASE_PATH = '/';
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUserData();
  }, []);

  const redirectToLogin = () => {
    window.location.replace(APP_BASE_PATH);
  };

  const fetchUserData = async () => {
    const token = apiService.getToken();
    const cachedUser = apiService.getUser();

    if (cachedUser) {
      setUser(cachedUser);
    }

    if (!token) {
      setLoading(false);
      redirectToLogin();
      return;
    }

    try {
      const response = await apiService.getAuthenticatedUser({ logoutOnUnauthorized: false });

      if (response.success && response?.data?.user) {
        setUser(response.data.user);
        apiService.setUser(response.data.user);
      } else if (response?.status === 401 || response?.status === 403) {
        // Keep optimistic token session during refresh and retry on next protected action.
        console.warn('Unauthorized during dashboard bootstrap; keeping token session.');
      } else if (!cachedUser) {
        redirectToLogin();
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      if (!cachedUser) {
        redirectToLogin();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to logout?')) {
      await apiService.logout();
      redirectToLogin();
    }
  };

  const memberSince = (() => {
    if (!user?.created_at) {
      return 'N/A';
    }

    const createdAt = new Date(user.created_at);
    if (Number.isNaN(createdAt.getTime())) {
      return 'N/A';
    }

    return createdAt.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  })();

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div className="header-content">
          <h1>Dashboard</h1>
          <button onClick={handleLogout} className="logout-btn">
            Logout
          </button>
        </div>
      </div>

      <div className="dashboard-content">
        <div className="welcome-card">
          <div className="user-avatar">
            {user?.avatar ? (
              <img src={user.avatar} alt={user.name} />
            ) : (
              <div className="avatar-placeholder">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <h2>Welcome back, {user?.name || 'there'}!</h2>
          <p className="user-email">{user?.email}</p>
        </div>

        <div className="info-grid">
          <div className="info-card">
            <div className="info-icon">👤</div>
            <h3>Profile</h3>
            <p>Manage your account settings</p>
          </div>

          <div className="info-card">
            <div className="info-icon">🔒</div>
            <h3>Security</h3>
            <p>Update password & security</p>
          </div>

          <div className="info-card">
            <div className="info-icon">📊</div>
            <h3>Analytics</h3>
            <p>View your activity</p>
          </div>

          <div className="info-card">
            <div className="info-icon">⚙️</div>
            <h3>Settings</h3>
            <p>Customize your experience</p>
          </div>
        </div>

        <div className="account-info">
          <h3>Account Information</h3>
          <div className="info-row">
            <span className="label">Name:</span>
            <span className="value">{user?.name}</span>
          </div>
          <div className="info-row">
            <span className="label">Email:</span>
            <span className="value">{user?.email}</span>
          </div>
          <div className="info-row">
            <span className="label">Member Since:</span>
            <span className="value">{memberSince}</span>
          </div>
          {user?.provider && (
            <div className="info-row">
              <span className="label">Login Method:</span>
              <span className="value">{user.provider}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
